import type { RegisteredRouter } from "@tanstack/react-router";
import { useMatchRoute } from "@tanstack/react-router";
import type { RouteToPath } from "@tanstack/router-core";
import { useCallback } from "react";

/**
 * TanStack Router's `useMatchRoute` only returns a boolean (or params). This hook
 * wraps it with a React Router `useRouteMatch`-like API: the returned callback
 * yields `null` when the route does not match, otherwise `{ isExact, params }`
 * where `isExact` is true only for an exact match (equivalent to `fuzzy: false`).
 */
export const useMatchRouteWithExact = () => {
  const matchRoute = useMatchRoute();

  return useCallback(
    <const TTo extends RouteToPath<RegisteredRouter>>(to: TTo) => {
      const match = matchRoute({ to, fuzzy: true });
      if (!match) return null;

      return {
        get isExact() {
          return Boolean(matchRoute({ to, fuzzy: false }));
        },
        params: match,
      };
    },
    [matchRoute],
  );
};
